import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Modal from "fast-modal-library";

const apiPrefix = process.env.REACT_APP_API_PREFIX;

export const buildForgotPassErrorMessage = (error) => {
  return "Une erreur est survenue : " + (error?.message || "Erreur inconnue");
};

export const hasRecaptchaEnterprise = (grecaptcha) => {
  return Boolean(grecaptcha && grecaptcha.enterprise);
};

const ForgotPassword = () => {
  const [username, setUsername] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [message, setMessage] = useState("");
  const navigate = useNavigate();
  const appNumber = useSelector((state) => state.app.appNumber);

  useEffect(() => {
    const script = document.createElement("script");
    script.src =
      "https://www.google.com/recaptcha/enterprise.js?render=6Lfz-I8qAAAAAKUsl8O_nRL6SByk9BRWb8JPstGl";
    script.async = true;
    document.head.appendChild(script);

    script.onload = () => {};

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const hide = () => {
    setShowModal(false);
    navigate("/signin");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Déterminer appNumber avec fallback
    let currentAppNumber = appNumber;
    if (!currentAppNumber) {
      const storedApp = localStorage.getItem("devAppNumber");
      if (storedApp) {
        currentAppNumber = parseInt(storedApp, 10);
      } else {
        const urlParams = new URLSearchParams(window.location.search);
        const appParam = urlParams.get("app");
        if (appParam) {
          currentAppNumber = parseInt(appParam, 10);
        } else {
          // Par défaut App4 pour localhost
          currentAppNumber = 4;
        }
      }
    }

    if (!currentAppNumber) {
      console.error("AppNumber non défini");
      setMessage("Erreur interne : appNumber non défini");
      setShowModal(true);
      return;
    }

    if (!window.grecaptcha || !window.grecaptcha.enterprise) {
      setMessage("reCAPTCHA non disponible. Veuillez recharger la page.");
      setShowModal(true);
      return;
    }

    // Fonction pour gérer la soumission avec reCAPTCHA
    const submitWithRecaptcha = async () => {
      try {
        const token = await window.grecaptcha.enterprise.execute(
          "6Lfz-I8qAAAAAKUsl8O_nRL6SByk9BRWb8JPstGl",
          { action: "LOGIN" }
        );

        const response = await fetch(apiPrefix + "forgot-password", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            username,
            token,
            appNumber: currentAppNumber,
          }),
        });

        let data;
        try {
          data = await response.json();
        } catch (parseError) {
          console.error("Erreur lors du parsing de la réponse:", parseError);
          setMessage(
            "Erreur lors de la réponse du serveur. Vérifiez votre connexion."
          );
          setShowModal(true);
          return;
        }

        if (response.ok) {
          setMessage(
            "Vous allez recevoir un mail vous indiquant la marche à suivre"
          );
        } else {
          setMessage(
            data.error || "Une erreur est survenue lors de la réinitialisation"
          );
        }
        setShowModal(true);
      } catch (error) {
        console.error("Erreur lors de la requête forgot-password:", error);
        setMessage(buildForgotPassErrorMessage(error));
        setShowModal(true);
      }
    };

    // Utiliser reCAPTCHA ready (ce n'est pas une promesse, juste un callback)
    /* istanbul ignore next */
    if (hasRecaptchaEnterprise(window.grecaptcha)) {
      try {
        window.grecaptcha.enterprise.ready(submitWithRecaptcha);
      } catch (error) {
        console.error("Erreur avec reCAPTCHA ready:", error);
        setMessage(
          "Erreur avec reCAPTCHA. Veuillez recharger la page et réessayer."
        );
        setShowModal(true);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="card-theme">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-theme-primary/10 mb-4">
              <i className="fas fa-key text-theme-primary text-xl"></i>
            </div>
            <h2 className="text-3xl font-bold text-theme-primary mb-2">
              Réinitialiser le mot de passe
            </h2>
            <p className="text-gray-600">
              Entrez votre adresse e-mail pour recevoir un lien de
              réinitialisation
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label
                htmlFor="username"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                <i className="fas fa-envelope mr-2 text-theme-accent"></i>
                Adresse e-mail
              </label>
              <input
                type="email"
                id="username"
                placeholder="votre@email.com"
                value={username}
                onChange={(e) => setUsername(e.target.value.trim())}
                className="input-theme"
                required
              />
            </div>

            <button type="submit" className="btn-theme w-full">
              <i className="fas fa-paper-plane mr-2"></i>
              Envoyer l'e-mail de réinitialisation
            </button>
          </form>

          {message && (
            <div
              className={`mt-4 p-4 rounded-lg ${
                message.includes("erreur") || message.includes("Erreur")
                  ? "bg-red-50 border border-red-200 text-red-700"
                  : "bg-green-50 border border-green-200 text-green-700"
              }`}
            >
              <i
                className={`fas ${
                  message.includes("erreur") || message.includes("Erreur")
                    ? "fa-exclamation-triangle"
                    : "fa-check-circle"
                } mr-2`}
              ></i>
              {message}
            </div>
          )}

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Vous vous souvenez de votre mot de passe ?{" "}
              <button
                onClick={() => navigate("/signin")}
                className="text-theme-accent hover:text-white font-medium transition-colors"
              >
                Se connecter
              </button>
            </p>
          </div>
        </div>

        {showModal && <Modal contentModal={message} hide={hide} />}
      </div>
    </div>
  );
};

export default ForgotPassword;
